<script setup>
/* eslint semi: 1 */
import { onMounted, getCurrentInstance, computed, ref } from 'vue';
import { useLocalStorageStore } from '@base-shared/stores/local-storage';
import Media from '@/components/pages/create/media/media-tmpl';
import MediaV2 from '@/components/pages/create/media-v2/index.vue';
import AuthMixin from '@/components/mixins/auth-mixin';
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin';
import CONST from '@/globals/constants.js';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useMixpanel } from '@base-shared/composables/use-mixpanel';

defineOptions({
  mixins: [AuthMixin, UnsavedDataMixin]
});

// Use component instance to call methods from the AuthMixin
const { proxy } = getCurrentInstance();
const localStorageStore = useLocalStorageStore();

const props = defineProps({
  mediaComponentAs: {
    type: String,
    default: CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,
    validator: value => [
      CONST.MEDIA_COMPONENT_AS_MEDIA_FILES,
      CONST.MEDIA_COMPONENT_AS_ALERT_FILES,
      CONST.MEDIA_COMPONENT_AS_LED_FILES,
      CONST.MEDIA_COMPONENT_AS_VIDEOWALL_FILES,
    ].includes(value)
  }
});

const isPageReady = ref(false);
const showMediaV2 = computed(() => localStorageStore.isAryaMediaV2Enabled);

function toggleMediaView (isLegacy) {
  proxy.checkUnsavedData({
    nextAction: () => {
      localStorageStore.setAryaMediaV2EnabledToStorage(!isLegacy);
      // Track the toggle event in Mixpanel with two properties:
      //  - version - legacy or the new version of the screen
      //  - mediaComponent - segregate between LED, alert, video wall and media files
      useMixpanel().track('Media Screen - Toggle', {
        version: isLegacy ? 'V1' : 'V2',
        mediaComponent: props.mediaComponentAs
      });
    }
  });
}

onMounted(() => {
  // Initialize the showMediaV2 state based on the value
  // stored in local storage
  localStorageStore.getAryaMediaV2EnabledFromStorage();
  proxy.isAuthenticated({
    callback: () => {
      isPageReady.value = true;

      // Track the visit event in Mixpanel with two properties:
      //  - version - legacy or the new version of the screen
      //  - mediaComponent - segregate between LED, alert, video wall and media files
      useMixpanel().track('Media Screen - Visit', {
        version: showMediaV2.value ? 'V2' : 'V1',
        mediaComponent: props.mediaComponentAs
      });
    }
  });
});

/**
 * Note: Move the route guards for unsaved data when the
 * legacy view switcher is removed.
 */
onBeforeRouteLeave((to, from) => {
  return new Promise((resolve) => {
    proxy.checkUnsavedData({
      nextAction: () => resolve(true),
      cancelAction: () => resolve(false)
    });
  });
});

onBeforeRouteUpdate((to, from) => {
  return new Promise((resolve) => {
    proxy.checkUnsavedData({
      nextAction: () => resolve(true),
      cancelAction: () => resolve(false)
    });
  });
});
</script>

<template>
  <template v-if="isPageReady">
    <MediaV2
      v-if="showMediaV2"
      :media-component-as="props.mediaComponentAs"
      @toggle-to-legacy-media="toggleMediaView(true)"
    />
    <Media
      v-else
      :media-component-as-prop="props.mediaComponentAs"
      @toggle-to-media-v2="toggleMediaView(false)"
    />
  </template>
</template>
